import { PowerAppsTriggerEventNames, usePowerAppsTrigger } from '@/context/PowerApps';
import { useLog } from '@/features/shared/Hooks/useLog';
import { useMounted } from '@/hooks/useMounted';
import { CMS_BASE_URL, SSO_RESOURCEAPI_ROOT } from '@/utils/constants';
import React from 'react';
import { ContactInfo } from './Contact.types';
import { useContactManager } from './useContactManager';
import { useContactResetInterceptor } from './useContactResetInterceptor';
import { useContactStatusInterceptor } from './useContactStatusInterceptor';
import { usePortalHomepageRedirectionInterceptor } from './usePortalHomepageRedirectionInterceptor';
import { useUserRegistrationStatus } from './useUserRegistrationStatus';

type ContactContextProps = {
  contactInfo: ContactInfo | null;
  updateContactInfo: ReturnType<typeof useContactManager>['updateContactInfo'];
  clearContactInfo: ReturnType<typeof useContactManager>['clearContactInfo'];
  isLoading: boolean;
  isForbidden: boolean;
  requireContactInfo: ReturnType<typeof useContactManager>['loadContactInfo'];
  registrationStatus: ReturnType<typeof useUserRegistrationStatus>;
};

const TRUSTED_DOMAINS = [
  SSO_RESOURCEAPI_ROOT,
  `${CMS_BASE_URL}/restservices/portal`,
  `!${CMS_BASE_URL}/restservices/portal/contact`,
  `!${CMS_BASE_URL}/restservices/portal/authorize`,
];

export const ContactContext = React.createContext<ContactContextProps>({
  contactInfo: null,
  updateContactInfo: () => Promise.resolve(null),
  clearContactInfo: () => {},
  isLoading: true,
  isForbidden: false,
  requireContactInfo: () => Promise.resolve(),
  registrationStatus: {
    isApproved: false,
    isPending: false,
    isDenied: false,
    isRegistered: false,
    isInvited: false,
    statusCode: 0,
  },
});

export const ContactProvider = ({ children }: React.PropsWithChildren<any>) => {
  const isMounted = useMounted();
  const {
    contactInfo,
    clearContactInfo,
    loadContactInfo,
    updateContactInfo,
    isLoading,
    isForbidden,
  } = useContactManager();

  const log = useLog({ context: 'ContactProvider' });
  const shouldPreventRender = React.useMemo(() => {
    return isLoading && contactInfo === null;
  }, [contactInfo, isLoading]);

  const registrationStatus = useUserRegistrationStatus(contactInfo);

  /**
   * Handle Contact Required from Interceptor
   */
  const handleContactRequired = React.useCallback(async () => {
    log('Contact Information required');
    loadContactInfo();
  }, [loadContactInfo, log]);

  /**
   * Handle Contact Reset from Interceptor
   */
  const handleContactReset = React.useCallback(() => {
    clearContactInfo();
  }, [clearContactInfo]);

  /**
   * Intercept calls to specific URLs resetting contact information
   */
  useContactResetInterceptor({
    onReset: handleContactReset,
    trustedDomains: [`${CMS_BASE_URL}/restservices/portal/authorize`],
  });

  /**
   * Intercept calls to specific URLs resetting contact information
   */
  useContactStatusInterceptor({
    contactInfo,
    trustedDomains: TRUSTED_DOMAINS,
  });

  /**
   * Intercept calls to resourceapi and modify URLs based on contact's webroles
   * for portal homepage redirection
   */
  usePortalHomepageRedirectionInterceptor({
    contactInfo,
    trustedDomains: [SSO_RESOURCEAPI_ROOT],
  });

  /**
   * Handle PowerApps Trigger
   *
   * Updates contact information when a PowerApps trigger is fired
   * on form submission.
   */
  const handlePowerAppsTrigger = React.useCallback(
    ({ eventName }: any) => {
      if (eventName === PowerAppsTriggerEventNames.FORM_SUBMITTED) {
        console.log('Form submitted, require contact info');
        loadContactInfo();
      }
    },
    [loadContactInfo],
  );

  usePowerAppsTrigger(handlePowerAppsTrigger);

  return (
    <ContactContext.Provider
      value={{
        contactInfo,
        updateContactInfo,
        clearContactInfo,
        isLoading,
        isForbidden,
        requireContactInfo: handleContactRequired,
        registrationStatus,
      }}
    >
      {isMounted && !shouldPreventRender && children}
    </ContactContext.Provider>
  );
};

export const PreviewContactProvider = ({ children }: React.PropsWithChildren<any>) => (
  <ContactContext.Provider
    value={{
      contactInfo: null,
      updateContactInfo: () => Promise.resolve(null),
      clearContactInfo: () => {},
      isLoading: true,
      isForbidden: false,
      requireContactInfo: () => Promise.resolve(),
      registrationStatus: {
        isApproved: false,
        isPending: false,
        isDenied: false,
        isRegistered: false,
        isInvited: false,
        statusCode: 0,
      },
    }}
  >
    {children}
  </ContactContext.Provider>
);

export const useContact = () => {
  const contact = React.useContext(ContactContext);

  if (!contact) {
    throw new Error('You need the ContactProvider');
  }

  return contact;
};
